/**
 * Hook for detecting media type from card data.
 */

import { useMemo } from 'react';
import { MediaType } from '../types';

/**
 * Media type keywords for detection
 */
const MEDIA_TYPE_KEYWORDS: Record<MediaType, string[]> = {
  book: ['book', 'books', 'ebook', 'audiobook'],
  movie: ['movie', 'movies', 'film', 'tv series', 'tv_series', 'tv mini series', 'tv show', 'tv shows'],
  music: ['music', 'album', 'song', 'track'],
  podcast: ['podcast', 'podcasts', 'episode'],
  default: [],
};

/**
 * Detect media type from a list of media tag names.
 * @param mediaTagNames - Array of media tag names from the card
 * @returns The detected media type
 */
export function getMediaType(mediaTagNames: string[] | null | undefined): MediaType {
  if (!mediaTagNames || mediaTagNames.length === 0) {
    return 'default';
  }

  const primary = mediaTagNames[0]?.toLowerCase().trim();
  if (!primary) {
    return 'default';
  }

  // Check each media type's keywords
  const matchedType = Object.entries(MEDIA_TYPE_KEYWORDS).find(
    ([mediaType, keywords]) => mediaType !== 'default' && keywords.some((keyword) => primary.includes(keyword))
  );

  return matchedType ? (matchedType[0] as MediaType) : 'default';
}

/**
 * Hook to get media type from card's mediaTagNames.
 * Memoized to prevent unnecessary recalculations.
 *
 * @param mediaTagNames - Array of media tag names from the card
 * @returns The detected media type
 */
export function useMediaType(mediaTagNames: string[] | null | undefined): MediaType {
  return useMemo(() => getMediaType(mediaTagNames), [mediaTagNames]);
}

export default useMediaType;
